import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Borrower } from '../../../../models/borrower/borrower.model';
import { Activity } from 'ng2-app/modules/activity/models/activity/activity.model';
import { ActivityService } from 'ng2-app/modules/activity/models/activity/activity.service';
import * as _ from 'lodash';
import { ChecklistOption } from 'ng2-app/components/checklist/checklist.component';
import { Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ActivityViewsService } from 'app/modules/funnel/models/activity-view.service';
import { GetDocumentCategories } from 'app/store/documents/document.actions';
import { Select, Store } from '@ngxs/store';
import {
  ActivityStreamEvent,
  ClearActivityStreamStore,
  GetStreamActivities, GetStreamActivityTypes
} from 'ng2-app/store/activity/activity.actions';
import { ActivityStreamState, ActivityStreamStateModel } from 'ng2-app/store/activity/activity-stream.state';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import { FormControl } from '@angular/forms';
import { sortBy } from 'lodash';

@Component( {
  selector: 'borrower-activity-stream',
  templateUrl: 'borrower-activity-stream.component.html',
  styleUrls: [ 'borrower-activity-stream.component.scss' ]
} )
export class BorrowerActivityStreamComponent implements OnInit, OnDestroy {

  @Input() borrower: Borrower;
  @Input() simplified: boolean = false;
  @Select( ActivityStreamState.activities ) activities$: Observable<Activity[]>;
  @Select( ActivityStreamState.activityTypes ) activityTypes$: Observable<ChecklistOption[]>;
  @Select( ActivityStreamState.loading ) activitiesLoading$: Observable<boolean>;


  activityTypesFormControl = new FormControl<Array<string>>([]);
  activityFocused: boolean;
  activities: Activity[] = [];
  pageSize = 50;
  page = 0;
  more = true;
  loading = false;
  error = false;
  destroyed$ = new Subject<boolean>();

  simplifiedTypes = [
    'call',
    'document',
    'message',
    'offerCreated',
    'sms',
    'status',
  ];

  constructor(
    private activityService: ActivityService,
    private activityViewsService: ActivityViewsService,
    private store: Store,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private eventBusService: EventBusService,
  ) {
  }

  ngOnInit() {
    this.loadActivities();
    this.stateSubscribe();
    this.filter = _.debounce( this.filter, 300 );
    this.activityViewsService.updateOrCreate(this.borrower.id, 'call').pipe(
      catchError(() => {
        return of();
      })
    ).subscribe();

    this.activities$.pipe(
      takeUntil( this.destroyed$ ),
    ).subscribe( () => {
      const routeActId = this.route.snapshot.queryParams?.activityId;
      return routeActId && !this.activityFocused
        ? setTimeout( () => this.focusSpecificActivity( Number( routeActId ) ) )
        : false;
    } );

    this.eventBusService.subscribe( this.eventBusService.types.GetDocReviewTaskErrorEvent ).pipe(
      takeUntil(this.destroyed$),
    ).subscribe( ({error}) => {
      this.snackBar.open(error, '', {
        panelClass: 'lendio-alert--error'
      });
    });

    this.fetchActivityTypes();
  }

  focusSpecificActivity( routeActId: number ) {
    const activityElement = document.getElementById( `activity-${routeActId}` );
    if ( !this.activityFocused && activityElement ) {
      this.activityFocused = true;
      activityElement.scrollIntoView();
    }
  }

  ngOnDestroy() {
    this.unload();
    this.destroyed$.next( true );
    this.destroyed$.complete();
    this.store.dispatch(new ClearActivityStreamStore());
  }

  loadMore() {
    if ( !this.more ) {
      return;
    }
    this.page++;
    this.loadActivities();
  }

  filter() {
    this.unload();
    this.loadActivities(true);
  }

  clearFilter() {
    this.activityTypesFormControl.reset([]);
    this.filter();
  }

  private fetchActivityTypes() {
    const params = new HttpParams()
      .set( 'query', JSON.stringify( {
        criteria: {
          'activities.borrowerId': { '==': this.borrower.id }
        }
      } ) );

    this.store.dispatch( new GetStreamActivityTypes(params) );
  }

  private loadActivities(reset = false) {
    const criteria = Object.assign(
      {},
      this.borrowerCriteria(),
      this.typeCriteria()
    );

    if(reset) {
      this.page = 0;
    }

    this.store.dispatch( new GetStreamActivities( criteria, this.pageSize, this.pageSize * this.page, reset ) );
  }

  private unload() {
    this.activities = [];
    this.more = true;
    this.page = 0;
    this.loading = false;
    this.error = false;
  }

  private stateSubscribe() {
    this.activityService.forBorrower( this.borrower.id ).pipe(
      takeUntil( this.destroyed$ )
    ).subscribe( activity => {
      this.store.dispatch( new ActivityStreamEvent( activity ) );
    } );
  }

  private borrowerCriteria() {
    return {
      'activities.borrowerId': {
        '==': this.borrower.id
      }
    };
  }

  private typeCriteria() {
    const selected = this.simplified
      ? this.simplifiedTypes
      : this.activityTypesFormControl.value!;

    if ( selected.length === 0 ) {
      return {};
    }

    return {
      'activities.type': {
        'in': selected
      }
    };
  }
}
